import { isBlockedExtension } from '@optidev/shared-types';

// Claude API only supports these image types for vision
export const CLAUDE_SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
];

// Read file magic bytes to detect actual format
export const detectFileTypeFromMagicBytes = async (file: File): Promise<string | null> => {
  try {
    const buffer = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // --- Images ---

    // JPEG (FF D8 FF)
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      return 'image/jpeg';
    }

    // PNG (89 50 4E 47 0D 0A 1A 0A)
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47 &&
        bytes[4] === 0x0D && bytes[5] === 0x0A && bytes[6] === 0x1A && bytes[7] === 0x0A) {
      return 'image/png';
    }

    // GIF (GIF87a or GIF89a)
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
      return 'image/gif';
    }

    // RIFF-based: WebP or WAV
    if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
      if (bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
        return 'image/webp';
      }
      if (bytes[8] === 0x57 && bytes[9] === 0x41 && bytes[10] === 0x56 && bytes[11] === 0x45) {
        return 'audio/wav';
      }
    }

    // --- Documents ---

    // PDF (%PDF)
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return 'application/pdf';
    }

    // --- Archives ---

    // ZIP / DOCX / XLSX / PPTX (PK\x03\x04)
    if (bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04) {
      return 'application/zip';
    }

    // GZIP (1F 8B)
    if (bytes[0] === 0x1F && bytes[1] === 0x8B) {
      return 'application/gzip';
    }

    // 7z (37 7A BC AF 27 1C)
    if (bytes[0] === 0x37 && bytes[1] === 0x7A && bytes[2] === 0xBC && bytes[3] === 0xAF &&
        bytes[4] === 0x27 && bytes[5] === 0x1C) {
      return 'application/x-7z-compressed';
    }

    // RAR (Rar!)
    if (bytes[0] === 0x52 && bytes[1] === 0x61 && bytes[2] === 0x72 && bytes[3] === 0x21 &&
        bytes[4] === 0x1A && bytes[5] === 0x07) {
      return 'application/x-rar-compressed';
    }

    // --- Audio ---

    // MP3 with ID3 tag
    if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
      return 'audio/mpeg';
    }
    // MP3 sync bytes (no ID3)
    if (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0) {
      return 'audio/mpeg';
    }

    // OGG (OggS)
    if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
      return 'audio/ogg';
    }

    // FLAC (fLaC)
    if (bytes[0] === 0x66 && bytes[1] === 0x4C && bytes[2] === 0x61 && bytes[3] === 0x43) {
      return 'audio/flac';
    }

    // --- Video ---

    // MP4 / MOV (ftyp at offset 4)
    if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
      return 'video/mp4';
    }

    return null;
  } catch {
    return null;
  }
};

// Keep old name as alias for backwards compatibility within this module
export const detectImageTypeFromMagicBytes = detectFileTypeFromMagicBytes;

// Validate files before upload:
// 1. Reject blocked extensions (executables, scripts, macros)
// 2. Images: only allow Claude-supported types (jpeg, png, gif, webp) based on actual content
// 3. Non-images: allow through (handled via wget download, not vision API)
export const validateFilesForUpload = async (files: File[]): Promise<{
  valid: File[];
  invalid: { file: File; reason: string }[]
}> => {
  const valid: File[] = [];
  const invalid: { file: File; reason: string }[] = [];

  for (const file of files) {
    // Check blocked extensions first
    if (isBlockedExtension(file.name)) {
      const ext = file.name.split('.').pop()?.toUpperCase() || 'Unknown';
      invalid.push({
        file,
        reason: `.${ext} files are not allowed for security reasons.`,
      });
      continue;
    }

    // Check if it claims to be a Claude vision-compatible image (jpeg, png, gif, webp)
    // Only these 4 types need strict magic byte validation
    const isVisionImageByType = CLAUDE_SUPPORTED_IMAGE_TYPES.includes(file.type);
    const isVisionImageByExtension = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);

    if (isVisionImageByType || isVisionImageByExtension) {
      // Validate actual content using magic bytes to prevent spoofing
      const detectedType = await detectFileTypeFromMagicBytes(file);

      if (detectedType && CLAUDE_SUPPORTED_IMAGE_TYPES.includes(detectedType)) {
        valid.push(file);
      } else {
        const ext = file.name.split('.').pop()?.toUpperCase() || 'Unknown';
        const detectedName = detectedType ? detectedType.split('/')[1]?.toUpperCase() : 'unknown format';
        invalid.push({
          file,
          reason: detectedType
            ? `File is actually ${detectedName}, not ${ext}. Use JPEG, PNG, GIF, or WebP.`
            : `Unsupported format. Use JPEG, PNG, GIF, or WebP.`
        });
      }
    } else {
      // All other files (PDF, video, audio, SVG, ICO, BMP, TIFF, HEIC, etc.) - allow through
      valid.push(file);
    }
  }

  return { valid, invalid };
};
